"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { LayoutTemplate } from "lucide-react";
import type { StoreApi } from "zustand";

import { Locale, getDir } from "@/lib/localization";
import type {
    LomdaV3Block,
    LomdaV3Content,
    LomdaV3Metadata,
    LomdaV3Page,
} from "@/lib/utils/lomdaV3/types";
import {
    LomdaV3StoreContext,
    createLomdaV3Store,
    useLomdaV3Editor,
    type LomdaV3EditorState,
} from "@/features/lomdaV3Editor/store/useLomdaV3Editor";
import {
    BlockEditableProvider,
    BlockThumbnailProvider,
} from "@/features/lomdaV3Editor/hooks/useBlockEditable";
import { useLomdaV3Theme } from "@/features/lomdaV3Editor/hooks/useLomdaV3Theme";
import { useResolvedBlockGap } from "@/features/lomdaV3Editor/hooks/useGridGap";
import BlockView from "@/features/lomdaV3Editor/components/blocks/blockView";
import {
    pageBackgroundStyle,
    resolvePageBackground,
} from "@/features/lomdaV3Editor/components/preview/lomdaBackground";

/** The lomda content is laid out at this width, then scaled to fit the box. */
const DESIGN_WIDTH = 1024;

function safeParse<T>(s?: string | null): T | null {
    if (!s) return null;
    try {
        return JSON.parse(s) as T;
    } catch {
        return null;
    }
}

function Placeholder() {
    return (
        <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
            <LayoutTemplate className="size-8" />
        </div>
    );
}

/**
 * Read-only, scaled render of one page's blocks — laid out at DESIGN_WIDTH then
 * scaled to fit the container (top clipped). Renders whatever store is in
 * context: an isolated thumbnail store, or the editor's global store for a live
 * page thumbnail.
 */
function ScaledPageRender({
    blocks,
    background,
    dir,
}: {
    blocks: LomdaV3Block[];
    background: LomdaV3Page["background"];
    dir: "rtl" | "ltr";
}) {
    const themeVars = useLomdaV3Theme();
    const ref = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(0);
    // Match the real page's spacing (preview player uses the same).
    const blockGap = useResolvedBlockGap();

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const update = () => setScale(el.clientWidth / DESIGN_WIDTH);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            dir={dir}
            style={{ ...themeVars, ...pageBackgroundStyle(background, false) }}
            className="pointer-events-none relative h-full w-full select-none overflow-hidden bg-background"
        >
            {scale > 0 && (
                <div
                    style={{
                        width: DESIGN_WIDTH,
                        transform: `scale(${scale})`,
                        transformOrigin: dir === "rtl" ? "top right" : "top left",
                    }}
                >
                    <BlockThumbnailProvider>
                        <BlockEditableProvider editable={false}>
                            <div
                                dir={dir}
                                // text-start: the thumbnail sits inside a <button>
                                // (page card), which defaults to text-align:center —
                                // reset it so text mirrors the real content exactly.
                                className="flex flex-col px-8 py-12 text-start text-foreground"
                                style={{ gap: blockGap }}
                            >
                                {blocks.map((b) => (
                                    <BlockView key={b.id} block={b} />
                                ))}
                            </div>
                        </BlockEditableProvider>
                    </BlockThumbnailProvider>
                </div>
            )}
        </div>
    );
}

/** Snapshot thumbnail of a saved lomda (storage card / list / side sheet). */
export default function LomdaV3Thumbnail({
    lomda,
    lomdaId = "",
    lomdaName = "",
    userId = "",
}: {
    /** The saved row's raw JSON, parsed again only when it changes. */
    lomda?: { content?: string | null; metadata?: string | null } | null;
    lomdaId?: string;
    lomdaName?: string;
    userId?: string;
} = {}) {
    const rawContent = lomda?.content;
    const rawMetadata = lomda?.metadata;
    const content = useMemo(
        () => safeParse<LomdaV3Content>(rawContent),
        [rawContent]
    );
    const metadata = useMemo(
        () => safeParse<LomdaV3Metadata>(rawMetadata),
        [rawMetadata]
    );
    const firstPage = content?.pages?.[0];
    const store = useMemo<StoreApi<LomdaV3EditorState> | null>(() => {
        if (!content?.pages?.[0]) return null;
        const created = createLomdaV3Store();
        created.getState().initialize({
            userId,
            lomdaId,
            lomdaName,
            content,
            metadata: metadata ?? { language: "he" },
        });
        created.setState({ mode: "preview" });
        return created;
    }, [content, metadata, lomdaId, lomdaName, userId]);

    if (!firstPage || !store) return <Placeholder />;

    return (
        <LomdaV3StoreContext.Provider value={store}>
            <ScaledPageRender
                blocks={firstPage.blocks}
                background={firstPage.background}
                dir={getDir((metadata?.language ?? "he") as Locale)}
            />
        </LomdaV3StoreContext.Provider>
    );
}

/** Live thumbnail of one page, rendered from the editor's current store. */
export function LomdaV3PageThumbnail({ page }: { page: LomdaV3Page }) {
    const language = useLomdaV3Editor((s) => s.metadata.language);
    if (page.blocks.length === 0 && !resolvePageBackground(page.background)) {
        return <Placeholder />;
    }
    return (
        <ScaledPageRender
            blocks={page.blocks}
            background={page.background}
            dir={getDir(language as Locale)}
        />
    );
}
